import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { SiteFrame } from "@/components/AppShell";
import GetVerifiedModal from "@/components/GetVerifiedModal";
import { AssetGlyph, ChangePill, PriceCell, Sparkline, fmtCompact, fmtPrice } from "@/components/market/PriceCell";
import { useSparklines } from "@/components/market/useSparklines";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLiveMarkets } from "@/lib/useLiveMarkets";

const TABS = ["All", "Gainers", "Losers", "New listings"];
const QUOTES = ["USDT", "USDC", "BTC", "INR"];
const PAGE_SIZE = 25;
const FAV_LS_KEY = "paisacx_market_favs_v1";
const PREFIX = { USDT: "$", USDC: "$", BTC: "₿", INR: "₹" };

function useFavorites(user) {
  const [favs, setFavs] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(FAV_LS_KEY) || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!user?.id) return;
    api
      .get("/api/markets/favorites")
      .then(({ data }) => setFavs(data.symbols || []))
      .catch(() => {});
  }, [user?.id]);

  const toggle = useCallback(
    async (symbol) => {
      if (user?.id) {
        try {
          const { data } = await api.post(`/api/markets/favorites/${symbol}/toggle`);
          setFavs(data.symbols || []);
          return;
        } catch {
          /* fall back to this browser */
        }
      }
      setFavs((prev) => {
        const next = prev.includes(symbol) ? prev.filter((x) => x !== symbol) : [...prev, symbol];
        localStorage.setItem(FAV_LS_KEY, JSON.stringify(next));
        return next;
      });
    },
    [user?.id],
  );

  return [favs, toggle];
}

export default function Market() {
  const loc = useLocation();
  const { user } = useAuth();
  const [showVerify, setShowVerify] = useState(false);

  useEffect(() => {
    if (loc.state?.justRegistered) {
      setShowVerify(true);
      window.history.replaceState({}, document.title);
    }
  }, [loc.state]);

  return (
    <SiteFrame>
      <Markets user={user} />
      <GetVerifiedModal open={showVerify} onClose={() => setShowVerify(false)} />
    </SiteFrame>
  );
}

function Markets({ user }) {
  const [quote, setQuote] = useState("USDT");
  const { rows: all, newest, live, updatedAt } = useLiveMarkets(quote);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("All");
  const [favOnly, setFavOnly] = useState(false);
  const [sort, setSort] = useState({ key: "default", dir: -1 });
  const [page, setPage] = useState(1);
  const [favs, toggleWatch] = useFavorites(user);

  useEffect(() => setPage(1), [q, tab, favOnly, quote, sort]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = all.filter(
      (a) => !needle || (a.name || "").toLowerCase().includes(needle) || a.symbol.toLowerCase().includes(needle),
    );
    if (tab === "Gainers") list = list.filter((a) => a.change_pct_24h > 0);
    if (tab === "Losers") list = list.filter((a) => a.change_pct_24h < 0);
    if (tab === "New listings") list = list.filter((a) => newest.includes(a.symbol));
    if (favOnly) list = list.filter((a) => favs.includes(a.symbol));
    if (sort.key === "default") return list;
    const val = (a) =>
      ({
        price: a.price,
        change: a.change_pct_24h,
        volume: a.quote_volume_24h ?? a.volume_24h,
        high: a.high_24h,
      })[sort.key] ?? 0;
    return [...list].sort((a, b) => (val(a) - val(b)) * sort.dir);
  }, [all, newest, q, tab, favOnly, favs, sort]);

  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const sparkOf = useSparklines(pageRows.map((r) => r.symbol), quote);
  const prefix = PREFIX[quote] || "$";

  function head(key, labelText) {
    return (
      <button
        type="button"
        onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === -1 ? 1 : -1 }))}
        className={`w-full text-right transition hover:text-foreground ${sort.key === key ? "text-foreground" : ""}`}
      >
        {labelText}
        {sort.key === key ? (sort.dir === -1 ? " ↓" : " ↑") : ""}
      </button>
    );
  }

  const updatedLabel = updatedAt
    ? new Date(updatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : null;

  return (
    <div className="page-fade mx-auto w-full min-w-0 max-w-7xl overflow-x-hidden px-4 py-8 sm:py-10" data-testid="market-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold sm:text-3xl">Markets</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Live prices from the exchange feed. Buy and sell through P2P or the OTC desk.
          </p>
        </div>
        <span
          className={`inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
            live ? "border-up/40 text-up" : "border-border text-muted-foreground"
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${live ? "animate-pulse bg-up" : "bg-muted-foreground"}`} />
          {live ? "Live" : "Connecting…"}
          {updatedLabel ? ` · ${updatedLabel}` : null}
        </span>
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:mt-6 sm:flex-row sm:flex-wrap sm:items-center">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search asset or ticker"
          data-testid="mkt-search"
          className="w-full min-w-0 rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none focus:border-primary sm:w-64 sm:max-w-xs"
        />
        <div className="flex min-w-0 max-w-full flex-wrap gap-1 sm:rounded-lg sm:border sm:border-border sm:bg-surface-2 sm:p-1">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              data-testid={`mkt-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-md px-3 py-1.5 text-xs transition ${
                tab === t ? "brand-gradient text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground sm:border-0"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="flex min-w-0 max-w-full flex-wrap gap-1 sm:rounded-lg sm:border sm:border-border sm:bg-surface-2 sm:p-1" aria-label="Quote currency">
          {QUOTES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setQuote(c)}
              data-testid={`mkt-quote-${c}`}
              className={`rounded-md px-3 py-1.5 text-xs transition ${
                quote === c ? "brand-gradient text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground sm:border-0"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setFavOnly((f) => !f)}
          data-testid="mkt-favorites"
          className={`w-fit rounded-lg border px-3 py-2 text-xs transition ${
            favOnly ? "border-primary text-foreground" : "border-border text-muted-foreground hover:text-foreground"
          }`}
        >
          ★ Favorites
        </button>
      </div>

      <div className="mt-5 space-y-2 md:hidden">
        {pageRows.length === 0 ? (
          <p className="rounded-xl border border-border px-4 py-10 text-center text-sm text-muted-foreground">
            {all.length ? "No assets match that filter." : "Loading markets…"}
          </p>
        ) : (
          pageRows.map((a) => (
            <div key={a.pair} className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface/50 px-3 py-3 text-left">
              <button
                type="button"
                aria-label="Toggle watchlist"
                onClick={() => toggleWatch(a.symbol)}
                className={`shrink-0 ${favs.includes(a.symbol) ? "text-accent" : "text-muted-foreground/40"}`}
              >
                ★
              </button>
              <AssetGlyph symbol={a.symbol} image={a.image} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{a.name}</p>
                <p className="text-xs text-muted-foreground">{a.symbol}</p>
              </div>
              <div className="shrink-0 text-right">
                <PriceCell value={a.price} prefix={prefix} className="text-sm" />
                <div className="mt-1 flex justify-end">
                  <ChangePill value={a.change_pct_24h} />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-6 hidden min-w-0 overflow-x-auto rounded-xl border border-border bg-surface/40 md:block">
        <div className="min-w-[720px] lg:min-w-[860px]" data-testid="mkt-table">
          <div className="table-head grid grid-cols-[28px_1.4fr_1fr_100px_1fr_1fr] gap-3 px-4 py-2.5 lg:grid-cols-[28px_1.6fr_1fr_120px_1fr_1fr_90px]">
            <span />
            <span>Asset</span>
            <span className="text-right">{head("price", "Price")}</span>
            <span className="text-right">{head("change", "24h %")}</span>
            <span className="text-right">{head("volume", "24h volume")}</span>
            <span className="text-right">{head("high", "24h high")}</span>
            <span className="hidden text-right lg:block">Last 24h</span>
          </div>
          {pageRows.length === 0 ? (
            <p className="px-4 py-14 text-center text-sm text-muted-foreground">
              {all.length ? "No assets match that filter. Clear the search or switch tabs." : "Loading markets…"}
            </p>
          ) : (
            pageRows.map((a) => (
              <div
                key={a.pair}
                data-testid={`mkt-row-${a.symbol}`}
                className="terminal-row grid-cols-[28px_1.4fr_1fr_100px_1fr_1fr] gap-3 px-4 py-3 lg:grid-cols-[28px_1.6fr_1fr_120px_1fr_1fr_90px]"
              >
                <button
                  type="button"
                  aria-label="Toggle watchlist"
                  data-testid={`mkt-fav-${a.symbol}`}
                  onClick={() => toggleWatch(a.symbol)}
                  className={favs.includes(a.symbol) ? "text-accent" : "text-muted-foreground/40 hover:text-accent"}
                >
                  ★
                </button>
                <div className="flex min-w-0 items-center gap-3">
                  <AssetGlyph symbol={a.symbol} image={a.image} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{a.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {a.symbol}
                      {a.featured && <span className="ml-1.5 rounded bg-primary/15 px-1.5 py-px text-[10px] font-medium text-primary">Featured</span>}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <PriceCell value={a.price} prefix={prefix} className="text-sm" />
                </div>
                <div className="flex justify-end">
                  <ChangePill value={a.change_pct_24h} />
                </div>
                <div className="num text-right text-sm text-muted-foreground">
                  {prefix}
                  {fmtCompact(a.quote_volume_24h ?? a.volume_24h)}
                </div>
                <div className="num text-right text-sm text-muted-foreground">
                  {prefix}
                  {fmtPrice(a.high_24h)}
                </div>
                <div className="hidden items-center justify-end lg:flex">
                  <Sparkline data={sparkOf(a.symbol)} up={a.change_pct_24h >= 0} width={80} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {pages > 1 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <span className="num">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, rows.length)} of {rows.length} assets
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              data-testid="mkt-prev"
              className="rounded-lg border border-border bg-surface-2 px-3 py-1.5 transition hover:border-primary disabled:opacity-40"
            >
              Previous
            </button>
            <span className="num px-2">
              {page} / {pages}
            </span>
            <button
              type="button"
              disabled={page === pages}
              onClick={() => setPage((p) => p + 1)}
              data-testid="mkt-next"
              className="rounded-lg border border-border bg-surface-2 px-3 py-1.5 transition hover:border-primary disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <p className="mt-4 text-xs text-muted-foreground">
        Ready to trade?{" "}
        <Link to="/p2p" className="text-primary hover:underline">
          Open the P2P book
        </Link>
        {" · "}
        <Link to="/otc" className="text-primary hover:underline">
          Request an OTC quote
        </Link>
        .
      </p>
    </div>
  );
}
